/**
 * OS toast for calendar reminders — the chatNotification.js shape, Phase 1
 * subset: the notification plugin path only (no Windows deep-click Rust
 * command yet — owner Decision 8 deferred that to Phase 2; the plain plugin
 * toast shows fine on Windows, it just doesn't deep-jump on click).
 *
 * Tauri-only, all imports lazy (`await import(...)`) — this file is in
 * tauriImportsAreLazy.test.js's GUARDED_FILES.
 */
import { isTauri } from './tauri';

let permissionState = null; // 'granted' | 'denied' | null (unknown/not asked)

/** Lazily load the plugin only inside Tauri (keeps it out of the web bundle). */
async function loadPlugin() {
  if (!isTauri()) return null;
  try {
    return await import('@tauri-apps/plugin-notification');
  } catch {
    return null;
  }
}

/**
 * Ask for notification permission once (macOS prompts; Windows is implicit).
 * Safe to call repeatedly — caches the result. Returns true if granted.
 */
export async function ensureCalendarNotificationPermission() {
  if (permissionState === 'granted') return true;
  if (permissionState === 'denied') return false;
  const mod = await loadPlugin();
  if (!mod) return false;
  try {
    let granted = await mod.isPermissionGranted();
    if (!granted) {
      const res = await mod.requestPermission();
      granted = res === 'granted';
    }
    permissionState = granted ? 'granted' : 'denied';
    return granted;
  } catch {
    return false;
  }
}

/**
 * Show one OS toast for a due reminder. The caller decides WHETHER to notify
 * (focus + desktopNotifications pref gating lives in the scheduler); this
 * just displays it. No-op outside Tauri / without permission.
 */
export async function showCalendarReminderNotification({ title, body }) {
  if (!isTauri()) return;
  const mod = await loadPlugin();
  if (!mod) return;
  const granted = await ensureCalendarNotificationPermission();
  if (!granted) return;
  try {
    mod.sendNotification({ title, body });
  } catch {
    /* toast is best-effort; the in-app toast already fired */
  }
}
